import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import Avatar from "../../components/common/Avatar";
import ConfirmationModal from "../../components/club/ConfirmationModal";
import FifaVerifiedBadge from "../../components/common/FifaVerifiedBadge";
import useTranslate from "../../hooks/useTranslate";
import shortRelativeTime from "../../components/lounge/relativeTime";
import { VotePill, YouChip } from "../../components/lounge/LoungePostCard";
import { ChatIcon, LoungeError } from "../../components/lounge/LoungeFeedStates";
import LoungeComment from "../../components/lounge/LoungeComment";
import LoungeKebabMenu from "../../components/lounge/LoungeKebabMenu";
import LoungeInlineEdit from "../../components/lounge/LoungeInlineEdit";
import LoungeReportDialog from "../../components/lounge/LoungeReportDialog";
import LoungeReportedNotice from "../../components/lounge/LoungeReportedNotice";
import LoungeAttachments from "../../components/lounge/LoungeAttachments";
import { MentionText } from "../../components/lounge/mentionText";
import { loungeService } from "../../services/loungeService";
import { applyVote } from "../../components/lounge/voteMath";

// Agent Lounge — post detail + comments (QAL-T4-FE). Design source:
// Agent Lounge.dc.html frame 1c (desktop detail), 1e·2 (402px), 1l·2
// (360px), 1i·3-5 (comment typing/posted); decisions in
// GATE0_ENTSCHEIDUNGEN.md + TICKET4_WORKORDER.
//
// Layout: desktop = ONE card (post section 20/22 paddings + avatar 42,
// divider, comment column 18px gaps, divider, sticky composer footer);
// mobile = post card + separate comments card (1e·2) with a FIXED bottom
// composer bar. Vote pills interactive since T5 (optimistic, server
// answer overwrites, rollback + toast on failure), NO kebab (T6),
// comments carry no counter in the input (L14) but the hard 1000
// maxLength.
//
// Reply-toggle default is per-viewport (L13, deliberate design
// divergence): desktop open, mobile closed — read once at mount.

const isDesktopViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia("(min-width: 640px)").matches;
};

const BackChevron = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M10 3.5L5.5 8l4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BackToLounge = () => {
  const { t } = useTranslate();
  return (
    <Link
      to="/lounge"
      className="text-[13px] font-medium text-[#2283FF] inline-flex items-center gap-1.5 w-fit min-h-[44px] sm:min-h-0 hover:underline"
    >
      <BackChevron />
      {t("Back to Lounge")}
    </Link>
  );
};

// Detail post header + body — bigger than the feed card (1c: avatar 42,
// text 15px; 1e·2: avatar 36, text 14.5; 1l·2: avatar 34, text 14).
// T6: kebab (desktop header per 1c Z.362, mobile footer per 1k·3) + edited
// stamps (header "· edited", desktop footer "Edited {t} ago" — 1m·3).
const DetailPost = ({
  post,
  commentCount,
  currentAccountId,
  onVote,
  onEdit,
  onDelete,
  onReport,
}) => {
  const { t, tv } = useTranslate();
  const author = post?.author || {};
  const channel = post?.channel || {};
  const isOwn =
    currentAccountId != null &&
    author.account_id != null &&
    Number(author.account_id) === Number(currentAccountId);
  const edited = Boolean(post?.edited_at);
  const editedRel = edited ? shortRelativeTime(post.edited_at) : "";
  const editedFooter =
    editedRel === "now"
      ? t("Edited just now")
      : tv("Edited {{time}} ago", { time: editedRel });
  const kebab = Boolean(onEdit || onDelete || onReport) && (
    <LoungeKebabMenu
      own={isOwn}
      type="post"
      onEdit={onEdit}
      onDelete={onDelete}
      onReport={onReport}
    />
  );

  return (
    <div className="p-[14px_13px] min-[402px]:p-[16px_15px] sm:p-[20px_22px] flex sm:gap-3">
      <div className="hidden sm:block shrink-0">
        <Avatar
          src={author.profile_picture_url}
          alt={author.name || ""}
          size={42}
          fallbackText={author.name}
        />
      </div>
      <div className="flex-1 min-w-0">
        {/* Mobile header — two lines, short channel tag (1e·2/1l·2). */}
        <div className="flex sm:hidden items-center gap-2">
          <div className="shrink-0">
            <Avatar
              src={author.profile_picture_url}
              alt={author.name || ""}
              size={36}
              fallbackText={author.name}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-[5px] min-w-0">
              <span className="text-[13px] min-[402px]:text-[13.5px] font-semibold text-[#111827] whitespace-nowrap overflow-hidden text-ellipsis">
                {author.name}
              </span>
              {author.is_fifa_verified === true && <FifaVerifiedBadge size="sm" />}
              {isOwn && <YouChip label={t("You")} />}
              <span className="text-[11.5px] min-[402px]:text-[12px] text-[#9CA3AF] whitespace-nowrap shrink-0">
                · {shortRelativeTime(post?.created_at)}
                {edited && <> · {t("edited")}</>}
              </span>
            </div>
            {author.agency_name ? (
              <div className="text-[11.5px] min-[402px]:text-[12px] text-[#6B7280] whitespace-nowrap overflow-hidden text-ellipsis">
                {author.agency_name}
              </div>
            ) : null}
          </div>
          {channel?.name && (
            <span className="text-[10.5px] min-[402px]:text-[11px] font-semibold text-[#2283FF] bg-[#EDF4FF] px-2 min-[402px]:px-[9px] py-[3px] rounded-full whitespace-nowrap shrink-0">
              {channel.short_name || channel.name}
            </span>
          )}
        </div>

        {/* Desktop header — one line, full channel name (1c). NO kebab. */}
        <div className="hidden sm:flex items-center gap-1.5 min-w-0">
          <span className="text-[14.5px] font-semibold text-[#111827] whitespace-nowrap">
            {author.name}
          </span>
          {author.is_fifa_verified === true && <FifaVerifiedBadge size="sm" />}
          {isOwn && <YouChip label={t("You")} />}
          {author.agency_name ? (
            <span className="text-[13px] text-[#6B7280] whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
              · {author.agency_name}
            </span>
          ) : null}
          <span className="text-[13px] text-[#9CA3AF] whitespace-nowrap">
            · {shortRelativeTime(post?.created_at)}
            {edited && <> · {t("edited")}</>}
          </span>
          <span className="flex-1" />
          {channel?.name && (
            <span className="text-[12px] font-semibold text-[#2283FF] bg-[#EDF4FF] px-2.5 py-[3px] rounded-full whitespace-nowrap shrink-0">
              {channel.name}
            </span>
          )}
          {kebab && <span className="hidden sm:inline-flex">{kebab}</span>}
        </div>

        <p className="mt-[9px] mb-[11px] min-[402px]:mt-2.5 min-[402px]:mb-3 sm:mt-2 sm:mb-3.5 text-[14px] min-[402px]:text-[14.5px] sm:text-[15px] leading-[1.6] text-[#111827] whitespace-pre-wrap break-words">
          <MentionText text={post?.text} />
        </p>

        {/* T7: image grid OR PDF row — identical to the feed card (1a). */}
        <LoungeAttachments
          attachments={post?.attachments}
          className="mb-[11px] min-[402px]:mb-3 sm:mb-3.5"
        />

        <div className="flex items-center gap-2.5 min-[402px]:gap-3 sm:gap-4">
          <VotePill
            score={post?.score ?? 0}
            myVote={post?.my_vote ?? null}
            onVote={onVote}
          />
          <span className="inline-flex items-center gap-[5px] sm:gap-1.5 text-[#6B7280] text-[12.5px] sm:text-[13px]">
            <ChatIcon size={14} stroke="currentColor" strokeWidth={1.4} />
            <span className="sm:hidden">{commentCount}</span>
            <span className="hidden sm:inline">
              {tv("{{count}} comments", { count: commentCount })}
            </span>
          </span>
          <span className="flex-1" />
          {/* Desktop footer edited stamp (1m·3); mobile keeps the header
              stamp only. */}
          {edited && (
            <span className="hidden sm:inline text-[12px] text-[#9CA3AF] whitespace-nowrap">
              {editedFooter}
            </span>
          )}
          {/* Mobile kebab in the actions row (1k·3). */}
          {kebab && <span className="sm:hidden inline-flex">{kebab}</span>}
        </div>
      </div>
    </div>
  );
};

// 404 / gone state — no drawn frame exists (design gap, kept minimal).
const PostGone = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  return (
    <div className="mt-4 bg-white border border-[#E4E9F0] rounded-[14px] py-12 px-6 flex flex-col items-center text-center">
      <span className="w-[52px] h-[52px] rounded-full bg-[#F1F5FA] flex items-center justify-center">
        <ChatIcon size={24} />
      </span>
      <p className="m-0 mt-3.5 text-[15px] font-semibold text-[#111827]">
        {t("This post is no longer available.")}
      </p>
      <p className="m-0 mt-[5px] text-[13.5px] text-[#6B7280]">
        {t("It may have been removed.")}
      </p>
      <button
        type="button"
        onClick={() => navigate("/lounge")}
        className="mt-4 bg-white border border-[#DDE3EB] rounded-full py-2 px-[18px] min-h-[44px] sm:min-h-0 text-[13px] font-semibold text-[#6B7280] hover:border-[#C6CFDA] hover:text-[#111827] whitespace-nowrap cursor-pointer"
      >
        {t("Back to Lounge")}
      </button>
    </div>
  );
};

// Skeleton while post + comments load — shimmer language from the feed.
const DetailSkeleton = () => (
  <div
    className="mt-4 bg-white border border-[#E4E9F0] rounded-[14px] p-[16px_15px] sm:p-[20px_22px] flex gap-3"
    data-testid="detail-skeleton"
  >
    <div className="tp-shimmer w-9 h-9 sm:w-[42px] sm:h-[42px] rounded-full shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="tp-shimmer h-4 w-[46%] rounded-md" />
      <div className="tp-shimmer h-3 w-full mt-3 rounded-md" />
      <div className="tp-shimmer h-3 w-[82%] mt-2 rounded-md" />
      <div className="tp-shimmer h-[30px] w-[92px] mt-[14px] rounded-full" />
    </div>
  </div>
);

const LoungePostDetail = () => {
  const { t, tv } = useTranslate();
  const { postId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Guards the one-shot jump-to-comment from an Activity item click (T8).
  const jumpedRef = useRef(false);
  const user = useSelector((state) => state?.auth?.user);
  const currentAccountId = user?.account_id || user?.id || null;

  const [post, setPost] = useState(null);
  // Channels feed the edit dropdown (T6) — the edit composer offers the
  // same channel switch as the create composer (1m·2).
  const [channels, setChannels] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  // Per-viewport reply-toggle default (L13) — read once at mount.
  const [defaultRepliesOpen] = useState(isDesktopViewport);
  const highlightTimer = useRef(null);
  const inputRef = useRef(null);
  // Per-target click sequence (T5) — with rapid clicks only the LATEST
  // request's server answer may overwrite the optimistic state.
  const postVoteSeq = useRef(0);
  const commentVoteSeq = useRef({});

  // ——— T6 moderation state ———
  const [postEditing, setPostEditing] = useState(false);
  const [postReported, setPostReported] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // {kind: "post"} | {kind: "comment", node, parentId|null}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // {kind: "post"} | {kind: "comment", node}
  const [reportTarget, setReportTarget] = useState(null);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportedCommentIds, setReportedCommentIds] = useState(() => new Set());
  const [undoingId, setUndoingId] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setNotFound(false);
    setError(false);
    (async () => {
      try {
        const [postRes, commentsRes] = await Promise.all([
          loungeService.getPost(postId, ctrl.signal),
          loungeService.getComments(postId, ctrl.signal),
        ]);
        if (ctrl.signal.aborted) return;
        if (postRes.success) {
          setPost(postRes.post);
          setCommentCount(postRes.post?.comment_count ?? 0);
          setComments(commentsRes.success ? commentsRes.results : []);
          if (!commentsRes.success) setError(true);
        } else if (postRes.status === 404) {
          setNotFound(true);
        } else {
          setError(true);
        }
      } catch (e) {
        if (e?.name === "CanceledError" || ctrl.signal.aborted) return;
        setError(true);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [postId]);

  // Channels once on mount — only the edit dropdown reads them.
  useEffect(() => {
    const ctrl = new AbortController();
    loungeService
      .getChannels(ctrl.signal)
      .then((res) => {
        if (res.success) setChannels(res.channels);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  const flashHighlight = (id) => {
    setHighlightId(id);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 2000);
  };

  // Jump-to-comment (T8, design 1o "a click jumps to the comment"): an Activity
  // item navigates here with #lounge-comment-<id>. Once comments are on screen,
  // scroll that row into view and flash the same #F8FBFF highlight a fresh
  // comment gets. One-shot per mount.
  useEffect(() => {
    if (loading || jumpedRef.current) return;
    const match = /^#lounge-comment-(\d+)$/.exec(location.hash || "");
    if (!match) return;
    const id = Number(match[1]);
    const el =
      typeof document !== "undefined"
        ? document.getElementById(`lounge-comment-${id}`)
        : null;
    if (!el) return;
    jumpedRef.current = true;
    if (typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    flashHighlight(id);
    // flashHighlight is defined inline and stable enough for a one-shot jump.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.hash, comments]);

  // Vote on the detail post (T5): optimistic first, server answer
  // overwrites, failure rolls back to the pre-click pair + toast.
  const handlePostVote = useCallback(
    async (value) => {
      if (!post) return;
      const prev = { score: post.score ?? 0, my_vote: post.my_vote ?? null };
      setPost((p) => (p ? { ...p, ...applyVote(prev.score, prev.my_vote, value) } : p));
      const seq = ++postVoteSeq.current;
      const res = await loungeService.votePost(postId, value);
      if (postVoteSeq.current !== seq) return; // a newer click owns the pill
      if (res.success) {
        setPost((p) => (p ? { ...p, score: res.score, my_vote: res.my_vote } : p));
      } else {
        setPost((p) => (p ? { ...p, ...prev } : p));
        toast.error(res.error || t("Something went wrong. Please try again."));
      }
    },
    [post, postId, t],
  );

  // Vote on a comment OR reply (T5) — one handler, the id decides which
  // node of the one-level tree gets patched.
  const patchCommentNode = (id, fields) =>
    setComments((list) =>
      list.map((c) => {
        if (c.id === id) return { ...c, ...fields };
        if (Array.isArray(c.replies) && c.replies.some((r) => r.id === id)) {
          return {
            ...c,
            replies: c.replies.map((r) => (r.id === id ? { ...r, ...fields } : r)),
          };
        }
        return c;
      }),
    );

  const handleCommentVote = useCallback(
    async (commentId, value) => {
      let target = null;
      for (const c of comments) {
        if (c.id === commentId) {
          target = c;
          break;
        }
        const reply = (c.replies || []).find((r) => r.id === commentId);
        if (reply) {
          target = reply;
          break;
        }
      }
      if (!target) return;
      const prev = { score: target.score ?? 0, my_vote: target.my_vote ?? null };
      const seq = (commentVoteSeq.current[commentId] =
        (commentVoteSeq.current[commentId] || 0) + 1);
      patchCommentNode(commentId, applyVote(prev.score, prev.my_vote, value));
      const res = await loungeService.voteComment(commentId, value);
      if (commentVoteSeq.current[commentId] !== seq) return;
      if (res.success) {
        patchCommentNode(commentId, { score: res.score, my_vote: res.my_vote });
      } else {
        patchCommentNode(commentId, prev);
        toast.error(res.error || t("Something went wrong. Please try again."));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comments, t],
  );

  // Top-level comment from the bottom bar — appends chronologically
  // (created_at ascending = end of list), brief #F8FBFF highlight,
  // post counter increments (1i·5).
  const sendComment = async () => {
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    const res = await loungeService.createComment(postId, { text: trimmed });
    setSubmitting(false);
    if (res.success && res.comment) {
      setComments((prev) => [...prev, { replies: [], ...res.comment }]);
      setCommentCount((c) => c + 1);
      setText("");
      flashHighlight(res.comment.id);
    }
  };

  // Reply under a top-level comment — parent enforced to the top-level id
  // (single level). Returns success so the inline composer can close.
  const submitReply = async (parentId, replyText) => {
    const res = await loungeService.createComment(postId, {
      text: replyText,
      parent: parentId,
    });
    if (res.success && res.comment) {
      setComments((prev) =>
        prev.map((c) =>
          c.id === parentId
            ? { ...c, replies: [...(c.replies || []), res.comment] }
            : c,
        ),
      );
      setCommentCount((c) => c + 1);
      flashHighlight(res.comment.id);
      return true;
    }
    return false;
  };

  // ——— T6 handlers ———

  const failToast = (res) =>
    toast.error(res.error || t("Something went wrong. Please try again."));

  // Post inline edit (1m·2) — the server's full object replaces the post.
  const savePostEdit = async ({ text, channel }) => {
    setSavingEdit(true);
    const res = await loungeService.updatePost(postId, { text, channel });
    setSavingEdit(false);
    if (res.success && res.post) {
      setPost(res.post);
      setPostEditing(false);
    } else {
      failToast(res);
    }
  };

  // Comment inline edit — text only.
  const saveCommentEdit = async (commentId, text) => {
    setSavingEdit(true);
    const res = await loungeService.updateComment(commentId, { text });
    setSavingEdit(false);
    if (res.success && res.comment) {
      // Keep the LOCAL replies array — the PATCH answer is the comment
      // object itself; whatever it carries under `replies` must not wipe
      // the loaded thread.
      const { replies, ...fields } = res.comment;
      patchCommentNode(commentId, fields);
      setEditingCommentId(null);
    } else {
      failToast(res);
    }
  };

  // Delete (white button, 1n·2): post → back to the feed (the whole thread
  // is gone for everyone); comment → node out of the tree, counter follows
  // (a dead top-level takes its replies with it, T4 rule).
  const performDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const res =
      deleteTarget.kind === "post"
        ? await loungeService.deletePost(postId)
        : await loungeService.deleteComment(deleteTarget.node.id);
    setDeleting(false);
    if (!res.success) {
      setDeleteTarget(null);
      failToast(res);
      return;
    }
    if (deleteTarget.kind === "post") {
      navigate("/lounge");
      return;
    }
    const { node, parentId } = deleteTarget;
    const removed = parentId == null ? 1 + (node.replies || []).length : 1;
    setComments((list) =>
      parentId == null
        ? list.filter((c) => c.id !== node.id)
        : list.map((c) =>
            c.id === parentId
              ? { ...c, replies: (c.replies || []).filter((r) => r.id !== node.id) }
              : c,
          ),
    );
    setCommentCount((c) => Math.max(0, c - removed));
    setDeleteTarget(null);
  };

  // Report submit — post: the whole detail collapses to the confirmation
  // row (server hides the detail with 404 after reload, §4); comment: the
  // node collapses in place.
  const submitReport = async (reason) => {
    if (!reportTarget || reportSubmitting) return;
    setReportSubmitting(true);
    const res =
      reportTarget.kind === "post"
        ? await loungeService.reportPost(postId, reason)
        : await loungeService.reportComment(reportTarget.node.id, reason);
    setReportSubmitting(false);
    if (!res.success) {
      failToast(res);
      return;
    }
    if (reportTarget.kind === "post") {
      setPostReported(true);
    } else {
      const id = reportTarget.node.id;
      setReportedCommentIds((prev) => new Set(prev).add(id));
    }
    setReportTarget(null);
  };

  const undoPostReport = async () => {
    if (undoingId) return;
    setUndoingId("post");
    const res = await loungeService.undoReport("post", postId);
    setUndoingId(null);
    if (res.success) setPostReported(false);
    else failToast(res);
  };

  const undoCommentReport = async (commentId) => {
    if (undoingId) return;
    setUndoingId(commentId);
    const res = await loungeService.undoReport("comment", commentId);
    setUndoingId(null);
    if (res.success) {
      setReportedCommentIds((prev) => {
        const next = new Set(prev);
        next.delete(commentId);
        return next;
      });
    } else {
      failToast(res);
    }
  };

  // Delete-dialog copy (1n·2 for posts; comment variant = workorder fill).
  const deleteCopy =
    deleteTarget?.kind === "post"
      ? {
          title: t("Delete this post?"),
          line: tv(
            "This removes the post and its {{count}} comments for everyone. It can't be undone.",
            { count: commentCount },
          ),
          keep: t("Keep post"),
        }
      : {
          title: t("Delete this comment?"),
          line:
            deleteTarget?.parentId == null &&
            (deleteTarget?.node?.replies || []).length > 0
              ? t(
                  "This removes the comment and its replies for everyone. It can't be undone.",
                )
              : t("This removes the comment for everyone. It can't be undone."),
          keep: t("Keep comment"),
        };

  return (
    <div className="w-full min-h-full bg-[#F8FAFC]">
      {/* App stage — caps the lounge at 1440px and centers it so the detail
          never swims in empty space on very wide screens (1g). The reading
          column below is 680px, widening to 820px only from 1536px. Nothing
          changes below 1536. */}
      <div className="max-w-[1440px] mx-auto">
        <div className="max-w-[680px] min-[1536px]:max-w-[820px] mx-auto px-3.5 min-[402px]:px-4 sm:px-5 pt-[18px] min-[402px]:pt-5 sm:pt-9 pb-[150px] sm:pb-14">
        <BackToLounge />

        {loading ? (
          <DetailSkeleton />
        ) : notFound ? (
          <PostGone />
        ) : error || !post ? (
          <div className="mt-4">
            <LoungeError />
          </div>
        ) : postReported ? (
          /* Reported post: the whole detail collapses to the 1h·2
             confirmation row — after a reload the server 404s it for this
             requester anyway (§4). Undo restores everything. */
          <div className="mt-3 min-[402px]:mt-3.5 sm:mt-4">
            <LoungeReportedNotice
              type="post"
              onUndo={undoPostReport}
              undoing={undoingId === "post"}
            />
          </div>
        ) : (
          <>
            {/* Desktop: ONE card carrying post, comments and the sticky
                composer footer (1c). Mobile: the same wrapper card holds
                the post; comments render as their own card below via
                margin + border tricks — see the section classes. */}
            <div className="mt-3 min-[402px]:mt-3.5 sm:mt-4 bg-transparent sm:bg-white sm:border sm:border-[#E4E9F0] sm:rounded-[14px]">
              {/* Post — own card on mobile (1e·2). */}
              <div className="bg-white border border-[#E4E9F0] rounded-[14px] sm:bg-transparent sm:border-0 sm:rounded-none">
                {postEditing ? (
                  /* The post section turns into the composer in place
                     (1m·2) — avatar column stays put on desktop. */
                  <div className="p-[14px_13px] min-[402px]:p-[16px_15px] sm:p-[20px_22px] flex sm:gap-3">
                    <div className="hidden sm:block shrink-0">
                      <Avatar
                        src={post?.author?.profile_picture_url}
                        alt={post?.author?.name || ""}
                        size={42}
                        fallbackText={post?.author?.name}
                      />
                    </div>
                    <LoungeInlineEdit
                      type="post"
                      initialText={post?.text || ""}
                      channels={channels}
                      initialChannelSlug={post?.channel?.slug || null}
                      submitting={savingEdit}
                      onCancel={() => setPostEditing(false)}
                      onSave={savePostEdit}
                    />
                  </div>
                ) : (
                  <DetailPost
                    post={post}
                    commentCount={commentCount}
                    currentAccountId={currentAccountId}
                    onVote={handlePostVote}
                    onEdit={() => setPostEditing(true)}
                    onDelete={() => setDeleteTarget({ kind: "post" })}
                    onReport={() => setReportTarget({ kind: "post" })}
                  />
                )}
              </div>

              <div className="hidden sm:block h-px bg-[#E4E9F0]" />

              {/* Comments — own card on mobile with row separators
                  (1e·2: padding 4px 15px), desktop 18px column (1c). */}
              <div className="mt-[9px] min-[402px]:mt-2.5 sm:mt-0 bg-white border border-[#E4E9F0] rounded-[14px] px-[13px] py-1 min-[402px]:px-[15px] sm:bg-transparent sm:border-0 sm:rounded-none sm:p-[18px_22px] sm:flex sm:flex-col sm:gap-[18px]">
                {comments.length === 0 ? (
                  /* Empty comments — no drawn frame (design gap, minimal). */
                  <p className="m-0 py-4 sm:py-1 text-center sm:text-left text-[13px] text-[#9CA3AF]">
                    {t("No comments yet.")}
                  </p>
                ) : (
                  comments.map((comment, i) => (
                    <LoungeComment
                      key={comment.id}
                      comment={comment}
                      currentAccountId={currentAccountId}
                      defaultRepliesOpen={defaultRepliesOpen}
                      highlightId={highlightId}
                      onSubmitReply={submitReply}
                      onVote={handleCommentVote}
                      isLast={i === comments.length - 1}
                      editingId={editingCommentId}
                      savingEdit={savingEdit}
                      onStartEdit={(node) => setEditingCommentId(node.id)}
                      onCancelEdit={() => setEditingCommentId(null)}
                      onSaveEdit={saveCommentEdit}
                      onDeleteRequest={(node, parentId) =>
                        setDeleteTarget({ kind: "comment", node, parentId })
                      }
                      onReportRequest={(node) =>
                        setReportTarget({ kind: "comment", node })
                      }
                      reportedIds={reportedCommentIds}
                      onUndoReport={undoCommentReport}
                      undoingId={undoingId}
                    />
                  ))
                )}
              </div>

              <div className="hidden sm:block h-px bg-[#E4E9F0]" />

              {/* Composer bar — desktop: sticky card footer (1c);
                  mobile: fixed over the bottom edge (1e·2/1l·2).
                  Enter OR Comment sends; no counter (L14), hard 1000 cap. */}
              <div className="fixed left-0 right-0 bottom-0 z-30 sm:sticky sm:z-10 sm:rounded-b-[14px] bg-white border-t border-[#E4E9F0] p-[11px_14px] min-[402px]:p-[12px_16px] sm:p-[14px_22px] flex items-center gap-2 min-[402px]:gap-[9px] sm:gap-2.5">
                <div className="hidden sm:block shrink-0">
                  <Avatar
                    src={user?.profile_picture_url || user?.profile_picture}
                    alt={user?.name || ""}
                    size={32}
                    fallbackText={user?.name}
                  />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={text}
                  maxLength={1000}
                  onChange={(e) => setText(e.target.value.slice(0, 1000))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      sendComment();
                    }
                  }}
                  placeholder={t("Add a comment…")}
                  className="flex-1 min-w-0 h-11 sm:h-auto border border-[#DDE3EB] rounded-full px-3.5 min-[402px]:px-4 sm:py-[9px] text-[13px] min-[402px]:text-[13.5px] text-[#111827] placeholder-[#9CA3AF] bg-white outline-none focus:border-[#2283FF] focus:shadow-[0_0_0_3px_rgba(34,131,255,0.14)]"
                  data-testid="comment-input"
                />
                <button
                  type="button"
                  onClick={sendComment}
                  disabled={!text.trim() || submitting}
                  className={`h-11 sm:h-auto bg-[#2283FF] text-white border-none rounded-[10px] px-3.5 min-[402px]:px-4 sm:py-[9px] sm:px-[18px] text-[13px] min-[402px]:text-[13.5px] font-semibold whitespace-nowrap shrink-0 ${
                    text.trim() && !submitting
                      ? "hover:bg-[#1B6FE0] cursor-pointer"
                      : "opacity-50 cursor-default"
                  }`}
                  data-testid="comment-send"
                >
                  {t("Comment")}
                </button>
              </div>
            </div>
          </>
        )}
        </div>
      </div>

      {/* Delete confirm (1n·2) — wording exact, buttons DELIBERATELY
          inverted: Delete = white secondary, Keep = blue primary. Centered
          dialog on mobile too (design note under 1n·2). */}
      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        variant="v2"
        title={deleteCopy.title}
        lines={[deleteCopy.line]}
        cancelText={t("Delete")}
        confirmText={deleteCopy.keep}
        confirmButtonClass="bg-[#2283FF] hover:bg-[#1B6FE0]"
        onClose={performDelete}
        onConfirm={() => !deleting && setDeleteTarget(null)}
      />

      {/* Report flow (1h desktop dialog / 1k mobile sheet). */}
      <LoungeReportDialog
        open={Boolean(reportTarget)}
        type={reportTarget?.kind === "comment" ? "comment" : "post"}
        submitting={reportSubmitting}
        onCancel={() => setReportTarget(null)}
        onSubmit={submitReport}
      />
    </div>
  );
};

export default LoungePostDetail;
